import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, X, Star, Zap, Users, Shield, Award } from "lucide-react";

const ComparisonTable = () => {
  const [activeTab, setActiveTab] = useState("features");

  const keyFeatures = [
    {
      category: "AI Technology",
      icon: <Zap className="w-5 h-5" />,
      features: [
        {
          name: "99% Accuracy AI Assessment",
          selfSync: true,
          traditional: false,
          description: "Advanced machine learning algorithms",
        },
        {
          name: "Real-Life Scenario Questions",
          selfSync: true,
          traditional: false,
          description: "Context-based behavioral assessment",
        },
        {
          name: "Dynamic Role Matching",
          selfSync: true,
          traditional: false,
          description: "AI-powered job fit recommendations",
        },
      ],
    },
    {
      category: "User Experience",
      icon: <Users className="w-5 h-5" />,
      features: [
        {
          name: "15-minute Setup",
          selfSync: true,
          traditional: false,
          description: "Quick deployment for immediate results",
        },
        {
          name: "Instant Results",
          selfSync: true,
          traditional: false,
          description: "Real-time personality insights",
        },
        {
          name: "Mobile-Optimized",
          selfSync: true,
          traditional: "partial",
          description: "Take assessments anywhere, anytime",
        },
      ],
    },
    {
      category: "Business Value",
      icon: <Award className="w-5 h-5" />,
      features: [
        {
          name: "3x Better Retention",
          selfSync: true,
          traditional: false,
          description: "Proven impact on employee retention",
        },
        {
          name: "Cost Per Assessment",
          selfSync: "Low",
          traditional: "High",
          description: "Starting at $3.99 per assessment",
        },
        {
          name: "No Long-term Contracts",
          selfSync: true,
          traditional: false,
          description: "Pay as you grow, cancel anytime",
        },
      ],
    },
  ];

  const planComparison = [
    {
      feature: "Assessment Credits",
      starter: "2 Free",
      smallTeam: "10",
      growingTeam: "50",
      scaleBusiness: "200",
      enterprise: "Unlimited",
    },
    {
      feature: "AI Accuracy",
      starter: "99%",
      smallTeam: "99%",
      growingTeam: "99%",
      scaleBusiness: "99%",
      enterprise: "99%",
    },
    {
      feature: "Team Analytics",
      starter: false,
      smallTeam: "Basic",
      growingTeam: "Advanced",
      scaleBusiness: "Custom",
      enterprise: "White-label",
    },
    {
      feature: "Support Level",
      starter: "Email",
      smallTeam: "Email",
      growingTeam: "Priority",
      scaleBusiness: "Dedicated",
      enterprise: "24/7",
    },
    {
      feature: "Integrations",
      starter: false,
      smallTeam: false,
      growingTeam: "Basic",
      scaleBusiness: "Advanced",
      enterprise: "Custom",
    },
    {
      feature: "Custom Reports",
      starter: false,
      smallTeam: false,
      growingTeam: false,
      scaleBusiness: true,
      enterprise: true,
    },
  ];

  const renderValue = (value, isHighlighted = false) => {
    if (value === true) {
      return (
        <Check
          className={`w-5 h-5 ${
            isHighlighted ? "text-yellow-400" : "text-green-600"
          }`}
        />
      );
    }
    if (value === false) {
      return <X className="w-5 h-5 text-gray-400" />;
    }
    if (value === "partial") {
      return (
        <span className="text-yellow-600 text-sm font-medium">Partial</span>
      );
    }
    return (
      <span
        className={`text-sm font-medium ${
          isHighlighted ? "text-white" : "text-gray-700"
        }`}
      >
        {value}
      </span>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex justify-center mb-8">
        <div className="bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab("features")}
            className={`px-6 py-2 rounded-xl font-medium transition-all ${
              activeTab === "features"
                ? "bg-gradient-to-r from-purple-600 to-cyan-600 text-white"
                : "text-gray-600 hover:text-purple-600"
            }`}
          >
            Why SelfSync?
          </button>
          <button
            onClick={() => setActiveTab("plans")}
            className={`px-6 py-2 rounded-xl font-medium transition-all ${
              activeTab === "plans"
                ? "bg-gradient-to-r from-purple-600 to-cyan-600 text-white"
                : "text-gray-600 hover:text-purple-600"
            }`}
          >
            Compare Plans
          </button>
        </div>
      </div>

      {activeTab === "features" && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8 max-md:px-2"
        >
          {keyFeatures.map((category, categoryIndex) => (
            <div
              key={categoryIndex}
              className="bg-white rounded-xl shadow-lg overflow-hidden"
            >
              <div className="bg-gradient-to-r from-purple-600 to-cyan-600 text-white p-4">
                <div className="flex items-center gap-3">
                  {category.icon}
                  <h3 className="text-xl font-bold">{category.category}</h3>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left p-4 font-semibold text-gray-700">
                        Feature
                      </th>
                      <th className="text-center p-4 font-semibold text-purple-600">
                        SelfSync AI
                      </th>
                      <th className="text-center p-4 font-semibold text-gray-700">
                        Traditional Tools
                      </th>
                      <th className="text-left p-4 font-semibold text-gray-700">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {category.features.map((feature, featureIndex) => (
                      <motion.tr
                        key={featureIndex}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: featureIndex * 0.1 }}
                        className="border-b hover:bg-gray-50"
                      >
                        <td className="p-4 font-medium text-gray-900">
                          {feature.name}
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex justify-center items-center">
                            {renderValue(feature.selfSync, true)}
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex justify-center items-center">
                            {renderValue(feature.traditional)}
                          </div>
                        </td>
                        <td className="p-4 text-gray-600 text-sm">
                          {feature.description}
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

        </motion.div>
      )}

      {activeTab === "plans" && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="px-2 shadow-lg overflow-hidden"
        >
          <div className="overflow-x-auto rounded-xl ">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-purple-600 to-cyan-600 text-white">
                  <th className="text-left p-4 font-semibold">Feature</th>
                  <th className="text-center p-4 font-semibold">Starter</th>
                  <th className="text-center p-4 font-semibold">Small Team</th>
                  <th className="text-center p-4 font-semibold relative">
                    Growing Team
                    <span className="absolute -top-1 left-1/2 -translate-x-1/2 bg-yellow-400 text-purple-900 px-2 py-1 rounded-full text-[10px] font-bold whitespace-nowrap">
                      Popular
                    </span>
                  </th>
                  <th className="text-center p-4 font-semibold relative">
                    Scale Business
                    <span className="absolute -top-1 left-1/2 -translate-x-1/2 bg-yellow-400 text-purple-900 px-2 py-1 rounded-full text-[10px] font-bold whitespace-nowrap">
                      Best Value
                    </span>
                  </th>
                  <th className="text-center p-4 font-semibold">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {planComparison.map((row, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`border-b hover:bg-gray-50 ${
                      index % 2 === 0 ? "bg-gray-50/50" : "bg-white"
                    }`}
                  >
                    <td className="p-4 font-medium text-gray-900">
                      {row.feature}
                    </td>
                    <td className="p-4 text-center">
                      {renderValue(row.starter)}
                    </td>
                    <td className="p-4 text-center">
                      {renderValue(row.smallTeam)}
                    </td>
                    <td className="p-4 text-center bg-purple-50">
                      {renderValue(row.growingTeam)}
                    </td>
                    <td className="p-4 text-center bg-purple-100">
                      {renderValue(row.scaleBusiness)}
                    </td>
                    <td className="p-4 text-center">
                      {renderValue(row.enterprise)}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-gray-50 p-6 text-center">
            <p className="text-gray-600 mb-4">
              All plans include 99% accurate AI assessments, instant results,
              and email support
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() =>
                  window.open("https://portal.selfsync.ai/pricing", "_blank")
                }
                className="bg-gradient-to-r from-purple-600 to-cyan-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-cyan-700 transition-colors shadow-lg"
              >
                View All Plans
              </button>
              <button
                onClick={() => window.open("/contact-us", "_blank")}
                className="border-2 border-purple-600 text-purple-600 px-6 py-3 rounded-lg font-semibold hover:bg-purple-600 hover:text-white transition-colors"
              >
                Contact Sales
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ComparisonTable;
